<template>
  <card-container
    :id="pageId"
    :loading-message="isChargeStep ? 'Charging Amount...' : 'Creating a Manual Order...'"
    :is-loading="isLoading"
    :disabled="isChargeStep ? isChargeStepDisabled : isOrderStepDisabled"
    @submit="onSubmit"
    @reset="resetForm"
  >
    <template #title>
      Create Manual Order
    </template>

    <template #card-notes>
      <div class="card-notes notification-message pt-1">
        <template v-if="isChargeStep">
          Please charge the customer before creating the manual order.<br>
          Enter the food amount from the DD order. The Gourmet Fee is based on the customer's plan.
        </template>
        <template v-else>
          Please create a manual order after you place an order on a DD account.<br>
          Please make sure that the phone number, DD account email and order UUID matches.<br>
          No need to put Preferred Contact Number and Client Name if it's the same as the customer number and name
        </template>
      </div>
    </template>

    <template #content>
      <template v-if="isChargeStep">
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Phone Number*</label>
          <phone-number-input
            v-model="gourmetInfo.phoneNumber"
            class="col-sm-7"
            :page-id="pageId"
            disabled
          />
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Amount ($)*</label>
          <div class="col-sm-5">
            <el-input
              id="charge-amount"
              v-model="chargeAmount"
              type="number"
              step="0.01"
              min="0.01"
              :disabled="orderHasBeenCharged"
            >
              <template #prepend>
                $
              </template>
            </el-input>
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Number of Items*</label>
          <div class="col-sm-2">
            <el-input
              id="item-count"
              v-model="itemCount"
              type="number"
              step="1"
              min="1"
              :required="!orderHasBeenCharged"
              :disabled="orderHasBeenCharged"
            />
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Gourmet Fee</label>
          <div class="col-sm-5">
            <el-input
              id="service-fee"
              :model-value="serviceFeeDisplay"
              disabled
            >
              <template #prepend>
                $
              </template>
            </el-input>
          </div>
        </div>
        <div class="row align-items-center">
          <div class="col-sm-8 offset-4">
            <el-checkbox
              id="orderHasBeenCharged"
              v-model="orderHasBeenCharged"
            >
              The order has been charged
            </el-checkbox>
          </div>
        </div>
        <div
          v-if="orderHasBeenCharged"
          class="row align-items-center"
        >
          <label class="col-sm-4 control-label">Transaction ID*</label>
          <div class="col-sm-7">
            <sanitized-input
              id="transactionId"
              v-model="transactionId"
              placeholder="Transaction ID"
              required
            />
          </div>
        </div>
      </template>

      <template v-else>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Phone Number*</label>
          <phone-number-input
            v-model="gourmetInfo.phoneNumber"
            class="col-sm-7"
            :page-id="pageId"
            disabled
          />
        </div>
        <div
          v-if="gourmetInfo.isRedelivery"
          class="row align-items-center"
        >
          <label class="col-sm-4 control-label">Nature of Complaint*</label>
          <div class="col-sm-7">
            <dropdown-list
              v-model="gourmetInfo.natureOfComplaint"
              required
              placeholder="Select Nature of Complaint"
              :options="gourmetNatureOfComplaintList"
            />
          </div>
        </div>
        <div
          v-if="gourmetInfo.isRedelivery"
          class="row align-items-center"
        >
          <label class="col-sm-4 control-label">Original Order ID</label>
          <div class="col-sm-7">
            <sanitized-input
              id="orderId"
              v-model="gourmetInfo.originalOrderId"
              placeholder="Original Order ID"
            />
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Order UUID*</label>
          <div class="col-sm-7">
            <sanitized-input
              id="orderId"
              v-model="gourmetInfo.orderId"
              placeholder="Order UUID"
              required
            />
          </div>
        </div>
        <div class="row">
          <label class="col-sm-4 control-label pt-10">Service Account Email*</label>
          <div class="col-sm-7">
            <email-input
              v-model="gourmetInfo.serviceEmail"
              placeholder="Service Account Email"
              required
              @update-validation="updateEmailValidation"
            />
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Preferred Contact Number</label>
          <phone-number-input
            id="gourmet-preferred-phone"
            v-model="gourmetInfo.preferredPhoneNumber"
            class="col-sm-7"
          />
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Client Name</label>
          <div class="col-sm-7">
            <sanitized-input
              id="name"
              v-model="gourmetInfo.clientName"
            />
          </div>
        </div>
        <div class="row align-items-center">
          <div class="col-sm-8 offset-4">
            <el-checkbox
              id="isRedelivery"
              v-model="gourmetInfo.isRedelivery"
            >
              Redelivery
            </el-checkbox>
          </div>
        </div>
        <div class="row align-items-center">
          <div class="col-sm-8 offset-4">
            <el-checkbox
              id="isBistro"
              v-model="gourmetInfo.isBistro"
            >
              Bistro
            </el-checkbox>
          </div>
        </div>
        <div
          v-if="gourmetInfo.isRedelivery"
          class="row align-items-center"
        >
          <div class="col-sm-8 offset-4">
            <el-checkbox
              id="doNotCharge"
              v-model="gourmetInfo.doNotCharge"
            >
              Do Not Charge
            </el-checkbox>
          </div>
        </div>
        <div class="row align-items-center">
          <div class="col-sm-8 offset-4">
            <el-checkbox
              id="noServiceFee"
              v-model="gourmetInfo.noServiceFee"
            >
              Do not charge Gourmet Fee
            </el-checkbox>
          </div>
        </div>
        <div class="row align-items-center">
          <div class="col-sm-8 offset-4">
            <el-checkbox
              id="applyDiscountedFee"
              v-model="gourmetInfo.applyDiscountedFee"
            >
              Apply discounted Gourmet Fee
            </el-checkbox>
          </div>
        </div>
        <div
          v-if="isReasonRequired"
          class="row"
        >
          <label class="col-sm-4 control-label pt-10"> Reason* </label>
          <div class="col-sm-7">
            <sanitized-textarea
              id="reason"
              v-model="gourmetInfo.reason"
              required
              placeholder="Reason"
              autosize
            />
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label no-padding-width">Note to LP</label>
          <div class="col-sm-7">
            <sanitized-textarea
              v-model="gourmetInfo.noteToLP"
              placeholder="Add internal notes for this order. Example: 'Cx celebrating anniversary.' or 'Cx had issues with past three orders'"
              autosize
            />
          </div>
        </div>
      </template>
    </template>

    <template #submit-button>
      {{ chargeStepButtonLabel }}
    </template>
  </card-container>
</template>

<script lang="ts">
import { ref, computed, watch, onBeforeUnmount, onMounted } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useGlobal } from '@/use/global';
import { useGourmet } from '@/use/gourmet';
import { gourmetNatureOfComplaintList } from '@/constants/deliveryOrder';
import { gourmetService } from '@/services/gourmet';
import { callerService } from '@/services/caller';
import { chargeUser } from '@/services/payment/chargeUser';
import { ChargeType } from '@/models/transaction';
import type { ServiceFeeResponse } from '@/services/caller/getServiceFee';

const pageId = 'create-manual-gourmet';

type GourmetFeeConfig = ServiceFeeResponse['gourmetConfig'];

const calculateGourmetFee = (config: GourmetFeeConfig, itemCount: number): number => {
  let feeCents = config.chargePerOrder.amount;

  if (config.unit === 'item') {
    feeCents += config.chargePerUnit.amount * itemCount;
  }

  if (config.maxCharge && feeCents > config.maxCharge.amount) {
    feeCents = config.maxCharge.amount;
  }

  return feeCents / 100;
};

export default {
  name: 'CreateManualGourmet',

  setup: () => {
    const { customerPhoneNumber, customerInfo } = useCustomer();
    const { redeliveryOrderId, setRedeliveryOrderId } = useGourmet();
    const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
    const { lastFormData, setFormData } = useGlobal(pageId);

    const gourmetInfo = ref({
      phoneNumber: '',
      orderId: '',
      serviceEmail: '',
      noServiceFee: false,
      doNotCharge: false,
      isBistro: false,
      applyDiscountedFee: false,
      isRedelivery: false,
      preferredPhoneNumber: '',
      clientName: '',
      reason: '',
      originalOrderId: '',
      natureOfComplaint: '',
      noteToLP: '',
    });
    const isLoading = ref(false);
    const isEmailValid = ref(true);
    const isChargeStep = ref(true);
    const chargeAmount = ref('');
    const itemCount = ref('');
    const orderHasBeenCharged = ref(false);
    const serviceFeeConfig = ref<GourmetFeeConfig | null>(null);
    const transactionId = ref('');

    const isPhoneNumberValid = computed(() => {
      return util.isPhoneNumberValid(gourmetInfo.value.phoneNumber);
    });

    const isPreferredPhoneNumberValid = computed(() => {
      return util.isPhoneNumberValid(gourmetInfo.value.preferredPhoneNumber, false);
    });

    const isReasonRequired = computed(() => {
      return (
        gourmetInfo.value.noServiceFee ||
        gourmetInfo.value.applyDiscountedFee ||
        gourmetInfo.value.doNotCharge
      );
    });

    const serviceFee = computed(() => {
      const parsedItemCount = Number(itemCount.value) || 0;
      if (!serviceFeeConfig.value) {
        return 0;
      }

      return calculateGourmetFee(serviceFeeConfig.value, parsedItemCount);
    });
    const serviceFeeDisplay = computed(() => serviceFee.value.toFixed(2));

    const totalChargeAmount = computed(() => {
      const amount = Number(chargeAmount.value) || 0;
      return amount + serviceFee.value;
    });

    const isChargeStepDisabled = computed(() => {
      if (isLoading.value || !isPhoneNumberValid.value) {
        return true;
      }

      if (orderHasBeenCharged.value) {
        return !transactionId.value.trim();
      }

      const amount = Number(chargeAmount.value);

      return !amount || amount <= 0 || !itemCount.value;
    });

    const chargeStepButtonLabel = computed(() => {
      if (!isChargeStep.value) {
        return 'Create Manual Order';
      }

      return orderHasBeenCharged.value ? 'Continue' : 'Charge';
    });

    const isOrderStepDisabled = computed(() => {
      return isLoading.value || !isPhoneNumberValid.value || !isPreferredPhoneNumberValid.value || !isEmailValid.value;
    });

    const loadServiceFee = async () => {
      const callerId = customerInfo.value?.id;

      if (!callerId) {
        serviceFeeConfig.value = null;
        return;
      }

      isLoading.value = true;
      try {
        const fees = await callerService.getServiceFee(callerId);
        serviceFeeConfig.value = fees.gourmetConfig;
      } catch {
        serviceFeeConfig.value = null;
      }
      isLoading.value = false;
    };

    const updateEmailValidation = (value: boolean) => {
      isEmailValid.value = value;
    };

    const continueWithoutCharge = () => {
      transactionId.value = transactionId.value.trim();
      isChargeStep.value = false;
    };

    const onSubmit = () => {
      if (isChargeStep.value) {
        if (orderHasBeenCharged.value) {
          return continueWithoutCharge();
        }

        return submitCharge();
      }

      return submitForm();
    };

    const submitCharge = async () => {
      const isChargeConfirmed = await showConfirmPopupDialog({
        text: `Are you sure you want to charge $${totalChargeAmount.value.toFixed(2)} to the customer (${gourmetInfo.value.phoneNumber})?`,
        title: 'Charge?',
        confirmButtonText: 'Charge',
      });

      if (!isChargeConfirmed) {
        return;
      }

      isLoading.value = true;
      try {
        const chargeTransactionId = await chargeUser({
          phoneNumber: gourmetInfo.value.phoneNumber,
          amount: totalChargeAmount.value,
          currency: customerInfo.value?.currency?.toLowerCase() || 'usd',
          notes: 'Manual gourmet order charge',
          category: ChargeType.Food,
          skipReceiptEmail: true,
          requireDeliveryOrderId: false,
        });

        if (!chargeTransactionId) {
          throw new Error('Charge failed. Please try again.');
        }

        transactionId.value = chargeTransactionId;
        isChargeStep.value = false;

        showSuccessPopupDialog({
          text: `$${totalChargeAmount.value.toFixed(2)} has been charged successfully.`,
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      isLoading.value = false;
    };

    const submitForm = async () => {
      if (gourmetInfo.value.isRedelivery && !gourmetInfo.value.originalOrderId) {
        const isConfirmed = await showConfirmPopupDialog({
          text: 'Are you sure you want to process re-delivery order without original order',
          title: 'Redelivery Order?',
        });

        if (!isConfirmed) {
          return;
        }
      }

      if (gourmetInfo.value.isRedelivery && !gourmetInfo.value.noServiceFee && !gourmetInfo.value.doNotCharge) {
        const isConfirmed = await showConfirmPopupDialog({
          text: 'Are you sure you want to process re-delivery order without "No Service Fee" or "Do Not Charge"',
          title: 'Redelivery Order?',
        });

        if (!isConfirmed) {
          return;
        }
      }

      const payload = {
        phoneNumber: gourmetInfo.value.phoneNumber,
        orderId: gourmetInfo.value.orderId,
        serviceEmail: gourmetInfo.value.serviceEmail,
        clientName: gourmetInfo.value.clientName,
        preferredPhoneNumber: gourmetInfo.value.preferredPhoneNumber || null,
        isRedelivery: gourmetInfo.value.isRedelivery,
        originalOrderId: gourmetInfo.value.isRedelivery ? gourmetInfo.value.originalOrderId : null,
        natureOfComplaint: gourmetInfo.value.isRedelivery ? gourmetInfo.value.natureOfComplaint : null,
        noteToLP: gourmetInfo.value.noteToLP || null,
        transactionId: transactionId.value,
        chargeOptions: {
          noServiceFee: gourmetInfo.value.noServiceFee,
          applyDiscountedFee: gourmetInfo.value.applyDiscountedFee,
          doNotCharge: gourmetInfo.value.doNotCharge,
          isBistro: gourmetInfo.value.isBistro,
          reason: gourmetInfo.value.reason,
        },
      };

      isLoading.value = true;
      try {
        await gourmetService.submitManualOrder(payload);

        showSuccessPopupDialog({
          text: 'The manual order has been created successfully.',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      isLoading.value = false;
    };

    const resetForm = () => {
      isChargeStep.value = true;
      chargeAmount.value = '';
      itemCount.value = '';
      orderHasBeenCharged.value = false;
      transactionId.value = '';
      gourmetInfo.value = {
        phoneNumber: customerPhoneNumber.value || '',
        orderId: '',
        serviceEmail: '',
        noServiceFee: false,
        doNotCharge: false,
        isBistro: false,
        applyDiscountedFee: false,
        isRedelivery: false,
        preferredPhoneNumber: '',
        clientName: '',
        reason: '',
        originalOrderId: '',
        natureOfComplaint: '',
        noteToLP: '',
      };
      loadServiceFee();
    };

    watch(customerPhoneNumber, resetForm);

    watch(
      () => customerInfo.value?.id,
      () => {
        if (isChargeStep.value) {
          loadServiceFee();
        }
      },
    );

    watch(
      () => gourmetInfo.value.isRedelivery,
      (value: boolean) => {
        if (!value) {
          gourmetInfo.value.doNotCharge = false;
        }
      },
      { immediate: true },
    );

    watch(
      () => gourmetInfo.value.doNotCharge,
      (value: boolean) => {
        if (value) {
          gourmetInfo.value.noServiceFee = false;
          gourmetInfo.value.applyDiscountedFee = false;
        }
      },
    );

    watch(
      () => gourmetInfo.value.noServiceFee,
      (value: boolean) => {
        if (value) {
          gourmetInfo.value.doNotCharge = false;
          gourmetInfo.value.applyDiscountedFee = false;
        }
      },
    );

    watch(
      () => gourmetInfo.value.applyDiscountedFee,
      (value: boolean) => {
        if (value) {
          gourmetInfo.value.doNotCharge = false;
          gourmetInfo.value.noServiceFee = false;
        }
      },
    );

    onMounted(() => {
      gourmetInfo.value = {
        phoneNumber: customerPhoneNumber.value || lastFormData.value?.phoneNumber,
        orderId: lastFormData.value?.orderId || '',
        serviceEmail: lastFormData.value?.serviceEmail || '',
        noServiceFee: lastFormData.value?.noServiceFee || false,
        doNotCharge: lastFormData.value?.doNotCharge || false,
        isBistro: lastFormData.value?.isBistro || false,
        applyDiscountedFee: lastFormData.value?.applyDiscountedFee || false,
        reason: lastFormData.value?.reason || '',
        preferredPhoneNumber: lastFormData.value?.preferredPhoneNumber || '',
        clientName: lastFormData.value?.clientName || '',
        originalOrderId: redeliveryOrderId.value ?? '',
        isRedelivery: !!redeliveryOrderId.value,
        natureOfComplaint: '',
        noteToLP: lastFormData.value?.noteToLP || '',
      };
      if (!gourmetInfo.value.isRedelivery) {
        gourmetInfo.value.doNotCharge = false;
      }
      loadServiceFee();
    });

    onBeforeUnmount(() => {
      setRedeliveryOrderId(null);
      setFormData(gourmetInfo.value);
    });

    return {
      pageId,
      gourmetInfo,
      isLoading,
      isChargeStep,
      chargeAmount,
      itemCount,
      orderHasBeenCharged,
      transactionId,
      chargeStepButtonLabel,
      serviceFeeDisplay,
      isPhoneNumberValid,
      isPreferredPhoneNumberValid,
      isReasonRequired,
      isChargeStepDisabled,
      isOrderStepDisabled,
      gourmetNatureOfComplaintList,
      isEmailValid,
      util,

      submitCharge,
      submitForm,
      onSubmit,
      resetForm,
      updateEmailValidation,
    };
  },
};
</script>
